import {
  findFacilityById,
  findPartnerById,
  findProfessionalById,
  findProfessionalRatingById,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  findSchedulesByProcedures,
} from '@/app/(main)/(pages)/busca/[slug]/_services/find-schedules-by-procedures.service';
import { mapToSearchPartnerData } from '@/app/(main)/(pages)/_services/search-api/map-to-search-partner-data.util';
import { CodesOrSlugsResponse } from '@/app/(main)/(pages)/busca/[slug]/_types/codes-or-slugs-response.type';
import {
  SearchApiProcedure,
  SearchApiProcedureType,
  SearchApiScheduleGroup,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { SearchPartnerData, SearchType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { getLogger } from '@/logger/logger';
import { TELEHEALTH_PROCEDURES } from '@/utils/consts/telemed.consts';

const TELEHEALTH_SUGGESTION_LIMIT = 2;

export type TelemedProcedure = {
  telemedProcedures: CodesOrSlugsResponse;
  schedules: SearchPartnerData[];
};

export type GETTelemedProcedureParams = {
  procedureCodes: string[];
};

export function hasRenderableTelemedSuggestions(
  telehealthSuggestions: TelemedProcedure | null,
): telehealthSuggestions is TelemedProcedure {
  return Boolean(
    telehealthSuggestions?.telemedProcedures?.length
    && telehealthSuggestions?.schedules?.length,
  );
}

function resolveTelemedCode(procedureCodes: string[]): string | undefined {
  if (!procedureCodes.length) {
    return undefined;
  }

  return Object.keys(TELEHEALTH_PROCEDURES).find((key) => TELEHEALTH_PROCEDURES[key] === procedureCodes[0]);
}

function buildTelemedProcedure(group: SearchApiScheduleGroup, telemedCode: string): SearchApiProcedure {
  const procedure = group.procedures[0];

  return {
    id: procedure?.id ?? '',
    name: procedure?.name ?? '',
    code: telemedCode,
    tussCode: '',
    slug: procedure?.slug ?? '',
    type: SearchApiProcedureType.NON_LABORATORY,
    availableForTelehealth: true,
  };
}

async function hydrateSchedule(group: SearchApiScheduleGroup, telemedCode: string): Promise<SearchPartnerData> {
  const procedure = buildTelemedProcedure(group, telemedCode);

  const [professional, partner, facility, rating] = await Promise.all([
    group.professionalId ? findProfessionalById(group.professionalId).catch(() => null) : Promise.resolve(null),
    group.partnerId ? findPartnerById(group.partnerId).catch(() => null) : Promise.resolve(null),
    group.partnerFacilityId ? findFacilityById(group.partnerFacilityId).catch(() => null) : Promise.resolve(null),
    group.professionalId ? findProfessionalRatingById(group.professionalId).catch(() => null) : Promise.resolve(null),
  ]);

  return mapToSearchPartnerData({
    group,
    procedures: [procedure],
    professional,
    partner,
    facility,
    rating,
    searchType: SearchType.ONLINE_CONSULTATION,
  });
}

function buildTelemedProcedures(group: SearchApiScheduleGroup, telemedCode: string): CodesOrSlugsResponse {
  const procedure = group.procedures[0];

  return [
    {
      id: procedure?.id ?? '',
      code: telemedCode,
      description: '',
      procedureType: '',
      tussCode: '',
      name: procedure?.name ?? '',
      slug: procedure?.slug ?? '',
      alias: '',
      keywords: [],
      specialties: [],
      availableForTelehealth: true,
      searchType: SearchType.ONLINE_CONSULTATION,
    },
  ];
}

export async function getTelemedProcedure(params: GETTelemedProcedureParams): Promise<TelemedProcedure | null> {
  const logger = getLogger('search.telemed-procedure');
  const telemedCode = resolveTelemedCode(params.procedureCodes);

  if (!telemedCode) {
    return null;
  }

  try {
    const { groups } = await findSchedulesByProcedures({
      procedureCodes: [telemedCode],
      limit: TELEHEALTH_SUGGESTION_LIMIT,
    });

    if (!groups.length) {
      return null;
    }

    const schedules = await Promise.all(groups.map((group) => hydrateSchedule(group, telemedCode)));

    return {
      telemedProcedures: buildTelemedProcedures(groups[0], telemedCode),
      schedules,
    };
  } catch (error) {
    logger.error('Failed to fetch telemed procedure suggestions', {
      search: {
        event: 'telemed_procedure_failed',
        procedure_codes: params.procedureCodes,
        telemed_code: telemedCode,
      },
      error,
    });

    return null;
  }
}
